import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Card, CardContent, CircularProgress, Alert, Chip } from '@mui/material';
import { ResponsiveSankey } from '@nivo/sankey';

interface MultiChannelAttributionProps {
  startDate: string;
  endDate: string;
}

const MultiChannelAttribution: React.FC<MultiChannelAttributionProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      setData({
        cross_channel_journeys: 342,
        avg_touchpoints: 2.8,
        sankeyData: {
          nodes: [
            { id: 'Email' }, { id: 'Chat' }, { id: 'Phone' }, { id: 'Social' },
            { id: 'Resolved' }, { id: 'Escalated' }
          ],
          links: [
            { source: 'Email', target: 'Chat', value: 120 },
            { source: 'Email', target: 'Resolved', value: 80 },
            { source: 'Chat', target: 'Phone', value: 45 },
            { source: 'Chat', target: 'Resolved', value: 150 },
            { source: 'Phone', target: 'Resolved', value: 90 },
            { source: 'Social', target: 'Chat', value: 60 }
          ]
        }
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (!data) return <Alert severity="info">No multi-channel data available</Alert>;

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Cross-Channel Journeys</Typography>
            <Typography variant="h4" color="primary">{data.cross_channel_journeys}</Typography>
            <Typography variant="caption">Customers using multiple channels</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Avg Touchpoints</Typography>
            <Typography variant="h4" color="primary">{data.avg_touchpoints}</Typography>
            <Typography variant="caption">Channels per customer</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Most Common Path</Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip label="Email" size="small" />
              <Chip label="→" size="small" variant="outlined" />
              <Chip label="Chat" size="small" />
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Customer Journey Flow (Sankey Diagram)</Typography>
        <Box height={400}>
          <ResponsiveSankey
            data={data.sankeyData}
            margin={{ top: 40, right: 160, bottom: 40, left: 50 }}
            align="justify"
            colors={{ scheme: 'category10' }}
            nodeOpacity={1}
            nodeThickness={18}
            nodeInnerPadding={3}
            nodeBorderWidth={0}
            nodeBorderRadius={3}
            linkOpacity={0.5}
            linkHoverOthersOpacity={0.1}
            enableLinkGradient={true}
          />
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2">💡 Multi-Channel Insights</Typography>
        <Typography variant="caption">Tracking customer movement across channels helps optimize resource allocation and identify preferred communication paths.</Typography>
      </Alert>
    </Box>
  );
};

export default MultiChannelAttribution;
